import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { GAME_CONFIG, type Choice, type RoomState } from '@majority-report/shared';
import { useGame } from '../net/GameProvider';
import { useCountdown } from '../hooks/useCountdown';
import { useQuestionText } from '../hooks/useQuestionText';
import { CountdownRing } from '../components/CountdownRing';
import { PlayerChip } from '../components/PlayerChip';
import { Banner, Card, Heading, Pill, Row, Stack, Subtle } from '../components/ui';
import { pop } from '../styles/GlobalStyle';

const Prompt = styled.h2`
  font-size: clamp(1.5rem, 5.2vw, 2.4rem);
  font-weight: 800;
  line-height: 1.18;
  text-align: center;
  letter-spacing: -0.015em;
`;

/** Side by side on anything wider than a phone, stacked below that. */
const Choices = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, 1fr);

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ChoiceButton = styled.button<{
  $side: Choice;
  $picked: boolean;
  $dimmed: boolean;
  $ghost: boolean;
}>`
  position: relative;
  min-height: 140px;
  padding: 26px 20px;
  border-radius: ${({ theme }) => theme.radii.lg};
  font-size: clamp(1rem, 3vw, 1.28rem);
  font-weight: 800;
  line-height: 1.3;
  transition: all 200ms ${({ theme }) => theme.transitions.bounce};

  ${({ theme, $side }) => {
    const palette = theme.choice[$side];
    return css`
      background: linear-gradient(160deg, ${palette.soft}, ${palette.base});
      color: ${palette.ink};
      box-shadow: 0 8px 0 ${palette.deep}, ${theme.shadows.puff};
    `;
  }}

  &:hover:not(:disabled) {
    transform: translateY(-4px) scale(1.015);
  }

  &:active:not(:disabled) {
    transform: translateY(8px);
    box-shadow: 0 0 0 transparent;
  }

  ${({ $picked, theme }) =>
    $picked &&
    css`
      transform: translateY(4px);
      outline: 4px solid ${theme.colors.ink};
      outline-offset: 3px;
    `}

  ${({ $dimmed }) =>
    $dimmed &&
    css`
      opacity: 0.5;
      filter: saturate(0.6);
    `}

  /* Ghosts still get working buttons, drained of colour so it is obvious the
     vote is for fun rather than for the tally. */
  ${({ $ghost }) =>
    $ghost &&
    css`
      filter: saturate(0.3) opacity(0.82);
    `}

  &:disabled {
    cursor: ${({ $picked }) => ($picked ? 'default' : 'not-allowed')};
  }
`;

const SideBadge = styled.span`
  position: absolute;
  top: 12px;
  left: 14px;
  font-size: 0.78rem;
  font-weight: 800;
  opacity: 0.55;
  letter-spacing: 0.12em;
`;

const PickedMark = styled.span`
  position: absolute;
  top: 10px;
  right: 12px;
  font-size: 22px;
  animation: ${pop} 320ms ${({ theme }) => theme.transitions.bounce} both;
`;

const PlayerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 9px;
`;

export function QuestionScreen({ state }: { state: RoomState }) {
  const { t } = useTranslation();
  const { actions, me, playerId } = useGame();
  const countdown = useCountdown(state.deadline, GAME_CONFIG.answerSeconds * 1000);
  const question = useQuestionText(state.question);

  // The server sends us our own answer (and nobody else's), so this survives a refresh.
  const myChoice = me?.choice ?? null;
  const hasAnswered = me?.hasAnswered ?? false;
  // Eliminated players still vote — it just doesn't count. Keeping the buttons
  // live is the point: being out shouldn't mean sitting on your hands.
  const isGhost = me !== null && !me.alive;
  // Players sitting out the round they arrived in are not being waited on, so
  // counting them would leave the tally permanently short of its total.
  const contenders = state.players.filter((p) => p.alive && !p.justJoined);
  const answered = contenders.filter((p) => p.hasAnswered).length;

  const modeReminder =
    state.settings.mode === 'minority-loses'
      ? t('question.modeReminderMinority')
      : t('question.modeReminderMajority');

  const pick = (choice: Choice) => {
    if (hasAnswered) return;
    actions.answer(choice);
  };

  return (
    <>
      <Row $justify="space-between" $wrap $gap="10px">
        <Row $gap="8px" $wrap>
          <Pill $tone="lavender">{t('question.roundLabel', { round: state.round })}</Pill>
          <Pill $tone={state.settings.mode === 'minority-loses' ? 'mint' : 'bubblegum'}>
            {modeReminder}
          </Pill>
          {question?.authorName && (
            <Pill $tone="lemon">✏️ {t('question.byAuthor', { name: question.authorName })}</Pill>
          )}
        </Row>
        <CountdownRing fraction={countdown.fraction} seconds={countdown.seconds} />
      </Row>

      <Card>
        <Stack $gap="22px">
          <Prompt>{question?.prompt ?? ''}</Prompt>

          <Choices>
            {question?.options.map(({ choice, text }) => (
              <ChoiceButton
                key={choice}
                type="button"
                $side={choice}
                $picked={myChoice === choice}
                $dimmed={hasAnswered && myChoice !== choice}
                $ghost={isGhost}
                disabled={hasAnswered}
                onClick={() => pick(choice)}
              >
                <SideBadge aria-hidden="true">{choice}</SideBadge>
                {text}
                {myChoice === choice && <PickedMark aria-hidden="true">👉</PickedMark>}
              </ChoiceButton>
            ))}
          </Choices>

          {isGhost ? (
            <Banner $tone="info">
              👻 {hasAnswered ? t('question.ghostLocked') : t('question.ghostVoting')}
            </Banner>
          ) : me?.justJoined && !hasAnswered ? (
            <Banner $tone="info">🆕 {t('question.sittingOut')}</Banner>
          ) : hasAnswered ? (
            <Banner $tone="info">🔒 {t('question.locked')}</Banner>
          ) : (
            <Subtle $center>{t('question.prompt')}</Subtle>
          )}
        </Stack>
      </Card>

      <Card>
        <Stack $gap="12px">
          <Row $justify="space-between" $wrap $gap="8px">
            <Heading>{t('lobby.playersTitle')}</Heading>
            <Pill $tone="mint">
              {t('question.answeredCount', { answered, total: contenders.length })}
            </Pill>
          </Row>
          <PlayerGrid>
            {state.players.map((player) => (
              <PlayerChip
                key={player.id}
                player={player}
                isYou={player.id === playerId}
                isHost={player.id === state.hostId}
                maxHearts={state.settings.startingHearts}
                showReady
              />
            ))}
          </PlayerGrid>
        </Stack>
      </Card>
    </>
  );
}
